import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';

import { Center, Loader, MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { kioskTheme, commanderResolver } from '@commander/ui';
import { useKioskConfigByCode, ApiError } from '@commander/api-client';

import { KioskLayout } from './components/KioskLayout';
import { KioskProvider } from './providers/KioskProvider';
import { SetupScreen } from './components/screens/SetupScreen';
import { DeactivatedScreen } from './components/screens/DeactivatedScreen';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30,
      refetchOnWindowFocus: true,
      retry: 2,
    },
  },
});

function extractKioskCode(): string | null {
  const match = window.location.pathname.match(/^\/k\/([a-z0-9-]+)\/?$/i);
  return match?.[1] ?? null;
}

function KioskAppWithCode({ code }: { code: string }) {
  const { data: config, isLoading, error } = useKioskConfigByCode(code);

  if (isLoading) {
    return (
      <Center h="100vh">
        <Loader size="xl" />
      </Center>
    );
  }

  if (error) {
    if (error instanceof ApiError) {
      if (error.isNotFound) {
        return <SetupScreen message="Kiosk not recognised. Please contact your administrator." />;
      }
      if (error.isForbidden) {
        return <DeactivatedScreen code={code} />;
      }
    }
    return <SetupScreen message="Unable to connect to the server. Please try again later." />;
  }

  if (!config) {
    return (
      <Center h="100vh">
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <KioskProvider kioskCode={config.code} locationId={config.locationId} locationName={config.locationName}>
      <KioskLayout />
    </KioskProvider>
  );
}

export function App() {
  const kioskCode = extractKioskCode();

  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider theme={kioskTheme} defaultColorScheme="light" cssVariablesResolver={commanderResolver}>
        <Notifications position="top-center" autoClose={5000} />
        {kioskCode ? <KioskAppWithCode code={kioskCode} /> : <SetupScreen />}
      </MantineProvider>
    </QueryClientProvider>
  );
}
